import { useRef, useEffect } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Terminal } from 'lucide-react';
import { DitheringShaderFill } from '../../components/ui/dithering-shader/dithering-shader-fill.js';
import { ButtonColorful } from '../../components/ui/button-colorful/button-colorful.js';
import { loadGsap, prefersReducedMotion } from '../../lib/gsap-setup.js';

const AGENT_BADGES = [
  { label: 'Claude Code', dot: 'bg-accent-lime' },
  { label: 'Cursor', dot: 'bg-accent-cyan' },
  { label: 'Codex CLI', dot: 'bg-accent-blue' },
  { label: 'Gemini CLI', dot: 'bg-accent-rose' },
];

export function HeroSection() {
  const { t } = useTranslation('landing');
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (prefersReducedMotion || !sectionRef.current) return;
    let cancelled = false;
    let ctx: { revert: () => void } | undefined;
    const el = sectionRef.current;
    // Mount-entry stagger (NOT scroll-triggered — the hero is always above
    // the fold). Uses the shared loadGsap() cache from Story 11.1 so we don't
    // duplicate the import('gsap') loader pattern across landing sections.
    void loadGsap()
      .then((gsap) => {
        if (cancelled) return;
        ctx = gsap.context(() => {
          gsap.from('.hero-rise', {
            y: 20,
            opacity: 0,
            duration: 0.8,
            stagger: 0.08,
            ease: 'power2.out',
          });
        }, el);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      ctx?.revert();
    };
  }, []);

  const headline = t('hero.headline');
  // A "|" in the translation marks an explicit line break (e.g. "Great code|still needs a face").
  // Shining gradient stays on the last word of the final line.
  const lines = headline.split('|').map((l) => l.trim());
  const lastLine = lines[lines.length - 1] ?? '';
  const leadingLines = lines.slice(0, -1);
  const lastLineWords = lastLine.split(' ');
  const tail = lastLineWords.pop() ?? '';
  const lastLineHead = lastLineWords.join(' ');

  return (
    <section ref={sectionRef} aria-labelledby="hero-heading" className="relative overflow-hidden">
      {/* Layered backdrop — swirl dither + vignette + edge fades */}
      <DitheringShaderFill
        shape="swirl"
        type="4x4"
        colorBack="#0a0a0a"
        colorFront="#2de1fc"
        pxSize={3}
        speed={0.6}
      />
      {/* Radial vignette so the centre reads darker behind the headline */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 70% 60% at 50% 45%, rgba(10,10,10,0.75), rgba(10,10,10,0.35) 55%, transparent 85%)',
        }}
      />
      {/* Top + bottom fades so the hero blends into header + next section */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-canvas to-transparent"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-48 bg-gradient-to-b from-transparent to-canvas"
      />

      <div className="relative mx-auto w-full max-w-[1280px] px-6 pt-24 pb-28 sm:pt-28 sm:pb-32">
        {/* Headline — split for shining-gradient accent on the last word */}
        <h1
          id="hero-heading"
          className="hero-rise mx-auto max-w-5xl text-center font-semibold leading-[1.02] tracking-[-0.035em] text-primary"
          style={{ fontSize: 'clamp(2.75rem, 7.2vw, 6rem)' }}
        >
          {leadingLines.map((line, i) => (
            <span key={String(i)} className="block">
              {line}
            </span>
          ))}
          <span className={leadingLines.length > 0 ? 'block' : undefined}>
            {lastLineHead}
            {lastLineHead ? ' ' : null}
            <span className="relative inline-block">
              <span className="agb-shine-text">{tail}</span>
              <span
                aria-hidden="true"
                className="pointer-events-none absolute -inset-x-4 -inset-y-2 rounded-xl bg-accent-lime/10 blur-2xl"
              />
            </span>
          </span>
        </h1>

        <p className="hero-rise mx-auto mt-6 max-w-2xl text-center text-body text-secondary">
          {t('hero.subheadline')}
        </p>

        {/* Agent compat strip — sits between the subheadline and the CTAs */}
        <div className="hero-rise mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-3">
          <span className="font-mono text-caption uppercase tracking-[0.2em] text-muted">
            {t('hero.worksWith')}
          </span>
          {AGENT_BADGES.map((a) => (
            <span
              key={a.label}
              className="inline-flex items-center gap-2 text-small text-secondary"
            >
              <span className={`h-1.5 w-1.5 rounded-full ${a.dot}`} aria-hidden="true" />
              {a.label}
            </span>
          ))}
        </div>

        {/* CTAs */}
        <div className="hero-rise mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link to="/sign-up" className="no-underline">
            <ButtonColorful
              className="h-11 px-5"
              endIcon={
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              }
            >
              {t('hero.cta')}
            </ButtonColorful>
          </Link>
          <a
            href="#terminal"
            className="inline-flex h-11 items-center gap-2 rounded-md border border-white/10 bg-canvas/70 px-4 text-sm font-medium text-primary backdrop-blur-md transition-colors hover:border-white/20 hover:bg-canvas/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
          >
            <Terminal className="h-4 w-4 text-secondary" aria-hidden="true" />
            {t('hero.secondaryCta')}
          </a>
        </div>
      </div>
    </section>
  );
}
